import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { A11yModule } from '@angular/cdk/a11y';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { MatButton, MatIconButton } from '@angular/material/button';
import { RouterLink } from '@angular/router';
import { TenantPerm } from '@enum/tenant/permission/tenant-perm.enum';
import { CreditsService } from '@shared/services/credits.service';
import { CreditSummary } from '@shared/services/governance-premium.models';
import { PermissionService } from '@shared/services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, filter, fromEvent, merge, of, switchMap, timeout, timer } from 'rxjs';
import { governanceFieldLabel, governanceValueLabel } from '../../views/pages/tenant/governance/governance-presentation';

/** Quanto a consulta do saldo pode levar antes de o seletor mostrar "Saldo indisponível". */
const TEMPO_LIMITE_DA_CONSULTA_MS = 15_000;

@Component({
  selector: 'mg-credits-balance',
  templateUrl: './credits-balance.component.html',
  styleUrl: './credits-balance.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [A11yModule, OverlayModule, MatButton, MatIconButton, MatIcon, RouterLink],
})
export class CreditsBalanceComponent implements OnInit {
  private readonly credits = inject(CreditsService);
  private readonly permissions = inject(PermissionService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly allowed = this.permissions.checkPermissions([TenantPerm.Credits_View]);
  readonly positions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 12 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -12 },
  ];
  wallet?: CreditSummary;
  opened = false;
  loading = true;

  get tariffs() {
    const included = ['scan_ai', 'governance_ai', 'chat_ai', 'billing_email', 'storage_monthly'];
    return (this.wallet?.tariffs ?? []).filter(tariff => !included.includes(tariff.service));
  }

  get activities() {
    const usage = this.wallet?.monthly_usage;
    return [
      { id: 'sms', label: 'SMS', used: usage?.sms.used, unit: 'mensagens enviadas' },
      { id: 'contracts', label: 'Contratos', used: usage?.contracts.used, unit: 'contratos enviados' },
      { id: 'whatsapp', label: 'WhatsApp', used: usage?.whatsapp.used, unit: 'mensagens enviadas' },
    ];
  }

  get periodLabel(): string {
    const period = this.wallet?.monthly_usage?.period ?? this.wallet?.billing_email_allowance?.period;
    if (!period) return 'Mês atual';
    const [year, month] = period.split('-').map(Number);
    const label = new Date(year, month - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  get attention(): boolean {
    return (this.wallet?.recovery_debt ?? 0) > 0;
  }

  ngOnInit(): void {
    if (!this.allowed) return;
    const visivel = (): boolean => document.visibilityState === 'visible';
    // A PRIMEIRA consulta não espera a aba ficar visível: aba aberta em segundo
    // plano (gravação, janela atrás de outra, aba restaurada) nunca dispara
    // `visibilitychange`, e o seletor ficava em "Carregando…" o uso inteiro.
    // Só a atualização periódica e a volta à aba pedem visibilidade.
    merge(
      of(null),
      timer(60_000, 60_000).pipe(filter(visivel)),
      this.credits.balanceChanges,
      fromEvent(document, 'visibilitychange').pipe(filter(visivel)),
    ).pipe(
      // Requisição que não responde vira o estado de erro (com "Tentar novamente"),
      // e não um "Carregando…" sem fim.
      switchMap(() => this.credits.summary().pipe(timeout(TEMPO_LIMITE_DA_CONSULTA_MS), catchError(() => of(null)))),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(summary => {
      this.wallet = summary ?? undefined;
      this.loading = false;
      this.cdr.markForCheck();
    });
  }

  retry(): void { this.credits.notifyBalanceChanged(); }
  close(): void { this.opened = false; }
  number(value: number | null | undefined): string { return value?.toLocaleString('pt-BR', { maximumFractionDigits: 1 }) ?? 'Não informado'; }
  tariffLabel(service: string): string { return governanceFieldLabel(service); }
  tariffUnitLabel(unit: string): string { return governanceValueLabel(unit); }

}
